import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styles from './AakashSoftware.module.css';
import { HelpingHand, Activity, Cpu ,ShieldCheck,
  TrendingUp,
  Settings,
  Lightbulb,
  Monitor,
  Expand,
  Layers,
  Network,
  Wrench,
  ClipboardCheck,
  Drone, IndianRupee } from 'lucide-react';
// Constants moved outside component to avoid recreation on each render
const CAROUSEL_IMAGES = [
  'Assets/Images/Software/Aakash_RC.svg',
  'Assets/Images/Software/Aakash_App.svg',
  'Assets/Images/Software/AakashDashboard.svg'
];

const CAROUSEL_INTERVAL = 3000;

const FEATURES_DATA = [
  {
    icon: 'Assets/Images/Software/aakash/icon1.svg',
    title: 'Improved Efficiency',
    description: 'Streamlines flight planning, monitoring, and data management, reducing manual effort and operational costs.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon2.svg',

    title: 'Enhanced Safety',
    description: 'Provides pre-flight check tools, real-time drone health monitoring, and hazard alerts.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon3.svg',
    title: 'Accurate Data & Analysis',
    description: 'Enables precise data collection from sensors and cameras, supporting detailed mapping, surveying, and inspections.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon4.svg',
    title: 'Centralized Management',
    description: 'Controls and manages multiple drones from a single platform.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon5.svg',
    title: 'Actionable Insights',
    description: 'Offers data-driven analytics to enhance strategic planning and operations.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon6.svg',
    title: 'Real-Time Monitoring & Reporting',
    description: 'Tracks flights in real time and generates comprehensive mission reports for better decision-making.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon7.svg',
    title: 'Operational Efficiency',
    description: 'Ensures optimized flight paths, seamless data transmission, and enhanced productivity.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon8.svg',
    title: 'Scalability',
    description: 'Adapts easily to growing fleets and expanding applications.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon9.svg',
    title: 'Flexible Deployment',
    description: 'Scales up or down based on demand and operational needs.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon10.svg',
    title: 'Seamless Network Integration',
    description: 'Connects with existing communication and logistics networks for expanded applications.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon11.svg',
    title: 'Maintenance Scheduling & Diagnostics',
    description: 'Ensures operational efficiency by proactively managing maintenance tasks and diagnosing potential issues.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon12.svg',
    title: 'Compliance Management',
    description: 'Automates flight log tracking and regulatory documentation, ensuring adherence to aviation standards.'
  },
  {
    icon: 'Assets/Images/Software/aakash/icon13.svg',
    title: 'Multi-Drone Management',
    description: 'Supports simultaneous operations, optimizing fleet coordination and performance.'
  }
];

// Memoized carousel component to prevent unnecessary re-renders
const ImageCarousel = React.memo(({ images, currentIndex, onDotClick }) => {
  const trackStyle = useMemo(() => ({
    transform: `translateX(-${currentIndex * (100 / images.length)}%)`
  }), [currentIndex, images.length]);

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselWrapper}>
        <div
          className={styles.carouselTrack}
          style={trackStyle}
        >
          {images.map((image, index) => (
            <div key={index} className={styles.carouselSlide}>
              <img
                src={image}
                alt={`Aakash software interface ${index + 1}`}
                className={styles.carouselImage}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.carouselDots}>
        {images.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${index === currentIndex ? styles.active : ''}`}
            onClick={() => onDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
});

// Memoized feature item component
const FeatureItem = React.memo(({ icon, title, description }) => (
  <div className={styles.featureItem}>
    <div className={styles.featureIcon}>
      <div className={styles.icon}>
        {typeof icon === 'string' ? (
          <img
            src={icon}
            alt={`${title} icon`}
            className={styles.iconImage}
            width="43"
            height="41"
          />
        ) : (
          icon
        )}
      </div>
    </div>
    <div className={styles.featureContent}>
      <p className={styles.featureTitle}>
        <strong>{title}</strong> –{' '}
        <span className={styles.featureDescription}>{description}</span>
      </p>
    </div>
  </div>
));

const AakashSoftware = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Memoized callback to prevent unnecessary re-renders
  const handleDotClick = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === CAROUSEL_IMAGES.length - 1 ? 0 : prevIndex + 1
      );
    }, CAROUSEL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.aakashContainer}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <h2 className={styles.mainTitle}>Aakash – axl's Unique Software Platform</h2>
        <p className={styles.subtitle}>Powering Autonomous Operations from Sky to System</p>

        <div className={styles.topCarouselSection}>
          <ImageCarousel
            images={CAROUSEL_IMAGES}
            currentIndex={currentIndex}
            onDotClick={handleDotClick}
          />
        </div>
      </div>

      {/* Description Section */}
      <div className={styles.descriptionSection}>
        <p className={styles.mainDescription}>
          We don't just make drones. We thought about the entire ecosystem around it. So Aakash isn't just flight planning software – it's a comprehensive end-to-end drone management platform that empowers you to take full control of your drone fleet. Designed for autonomous operations, seamless data exchange, and intelligent insights - Aakash transforms your operations through real-time data exchange. It sits features powerful tools for data processing and analysis, and offers smooth integration with leading ERP systems and existing customer infrastructures.
        </p>
      </div>

      {/* Middle Image with Person */}
      <div className={styles.middleImageSection}>
        <img
          src="Assets/Images/Software/Drone_flying.svg"
          alt="Person with laptop controlling drone operations"
          className={styles.middleImage}
          loading="lazy"
        />
      </div>

      {/* Why is it unique section */}
      <div className={styles.featuresSection}>
        <h3>Why is it unique?</h3>

        <div className={styles.featuresList}>
          {FEATURES_DATA.map((feature, index) => (
            <FeatureItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>

      {/* Bottom Image Section */}
      <div className={styles.bottomImageSection}>
        <img
          src="Assets/Images/Software/Aakash-dashboard-laptop.svg"
          alt="Laptop displaying drone software interface"
          className={styles.bottomImage}
          loading="lazy"
        />
         {/* Final Description */}
      <div className={styles.finalDescription}>
        <p>
          With Aakash, drone operators can maximize efficiency, maintain compliance, and streamline operations, making it an essential tool for enterprise-level drone management. Aakash empowers drone operators with greater control, reliability, and efficiency, ensuring mission success with every flight.
        </p>
      </div>
      </div>
    </div>
  );
};

export default AakashSoftware;